import { useGivingAccess } from "@/features/giving/treasureAccess";
import { useServiceNavigation } from "@/features/authorization/hooks/useServiceNavigation";
import {
  Activity,
  BarChart3,
  CalendarDays,
  Church,
  Database,
  HeartPulse,
  Home,
  LayoutDashboard,
  Settings,
  ShieldCheck,
  Users,
} from "lucide-react";
import { NavLink } from "react-router-dom";

import type { MinistryNavigationEntry } from "@/features/authorization/hooks/useMinistryNavigation";

import { ChurchInfoCard } from "./ChurchInfoCard";
import { canOpenPage } from "@/features/authorization/pageAccess";
import { useEffectivePermissions } from "@/features/authorization/hooks/useEffectivePermissions";

const navItems = [
  {
    label: "Overview",
    to: "/",
    icon: LayoutDashboard,
  },
  {
    label: "Members",
    to: "/members",
    icon: Users,
  },
  {
    label: "Households",
    to: "/households",
    icon: Home,
  },
  {
    label: "Attendance",
    to: "/attendance",
    icon: CalendarDays,
  },
  {
    label: "Services",
    to: "/services",
    icon: Church,
  },
  {
    label: "Care/Follow-up",
    to: "/care-queue",
    icon: HeartPulse,
  },
  {
    label: "Reports",
    to: "/reports",
    icon: BarChart3,
  },
];

const settingsItems = [
  {
    label: "Data Import",
    to: "/settings/data-import",
    icon: Database,
  },
  {
    label: "System Health",
    to: "/settings/system-health",
    icon: Activity,
  },
];

interface SidebarProps {
  ministries: MinistryNavigationEntry[];
  churchName: string;
  churchCity?: string | null;
  churchState?: string | null;
}

export function Sidebar({
  ministries,
  churchName,
  churchCity,
  churchState,
}: SidebarProps) {
  const { permissions } = useEffectivePermissions();
  const giving = useGivingAccess();
  const servicesPath = useServiceNavigation();
  const visibleSettings =
    permissions.has("ROLES_MANAGE") || permissions.has("MINISTRIES_MANAGE")
      ? [
          ...settingsItems,
          {
            label: "Administration",
            to: permissions.has("MINISTRIES_MANAGE")
              ? "/settings/administration/ministries"
              : "/settings/administration/roles",
            icon: ShieldCheck,
          },
        ]
      : settingsItems;
  return (
    <aside className="sticky top-0 hidden h-screen w-72 shrink-0 flex-col border-r border-slate-800 bg-slate-950 px-5 py-6 text-white md:flex">
      <div className="mb-4 shrink-0 px-1 py-1">
        <img
          src="/official_logo.png"
          alt="MinistryIQ"
          className="h-auto w-full max-w-[245px] object-contain"
        />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto pr-1">
        <nav className="space-y-2">
          {[...navItems, ...(giving.data?.permissions.length ? [{label:"Treasure",to:"/treasure",icon:ShieldCheck}] : [])].filter((item) => item.to === "/services" ? !!servicesPath : canOpenPage(item.to, permissions)).map((item) => {
            const Icon = item.icon;

            return (
              <NavLink
                key={item.to}
                to={item.to === "/services" ? servicesPath! : item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `group flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-medium transition ${
                    isActive
                      ? "bg-indigo-600/20 text-white ring-1 ring-indigo-500/40"
                      : "text-slate-400 hover:bg-slate-900 hover:text-white"
                  }`
                }
              >
                <span className="rounded-lg bg-slate-900 p-2 text-slate-400 transition group-hover:text-white">
                  <Icon className="h-4 w-4" />
                </span>

                {item.label}
              </NavLink>
            );
          })}
        </nav>

        {ministries.length > 0 && (
          <section
            className="mt-8"
            aria-labelledby="ministry-navigation-heading"
          >
            <h2
              id="ministry-navigation-heading"
              className="mb-3 px-4 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500"
            >
              Ministries
            </h2>
            <nav aria-label="Ministry workspaces" className="space-y-2">
              {ministries.map((ministry) => (
                <NavLink
                  key={ministry.id}
                  to={ministry.to}
                  className={({ isActive }) =>
                    `group flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-medium transition ${
                      isActive
                        ? "bg-indigo-600/20 text-white ring-1 ring-indigo-500/40"
                        : "text-slate-400 hover:bg-slate-900 hover:text-white"
                    }`
                  }
                >
                  <span className="rounded-lg bg-slate-900 p-2 text-slate-400 transition group-hover:text-white">
                    <Users className="h-4 w-4" />
                  </span>
                  <span className="min-w-0 break-words">{ministry.label}</span>
                </NavLink>
              ))}
            </nav>
          </section>
        )}

        <div className="mt-8">
          <div className="mb-3 flex items-center gap-2 px-4">
            <Settings className="h-4 w-4 text-slate-500" />

            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              Settings
            </p>
          </div>

          <nav className="space-y-2">
            {visibleSettings.filter((item) => canOpenPage(item.to, permissions)).map((item) => {
              const Icon = item.icon;

              return (
                <NavLink
                  key={item.to}
                  to={item.to === "/services" ? servicesPath! : item.to}
                  className={({ isActive }) =>
                    `group flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-medium transition ${
                      isActive
                        ? "bg-indigo-600/20 text-white ring-1 ring-indigo-500/40"
                        : "text-slate-400 hover:bg-slate-900 hover:text-white"
                    }`
                  }
                >
                  <span className="rounded-lg bg-slate-900 p-2 text-slate-400 transition group-hover:text-white">
                    <Icon className="h-4 w-4" />
                  </span>

                  {item.label}
                </NavLink>
              );
            })}
          </nav>
        </div>
      </div>

      <div className="shrink-0 border-t border-slate-800 pt-4">
        {churchName ? (
          <ChurchInfoCard
            name={churchName}
            city={churchCity}
            state={churchState}
          />
        ) : (
          <div className="rounded-2xl border border-slate-800 bg-slate-900/70 p-4 text-sm text-slate-500">
            Loading church...
          </div>
        )}
      </div>
    </aside>
  );
}
